import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI, userAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';

const UserPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [transactionPassword, setTransactionPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showTransactionPasswordModal, setShowTransactionPasswordModal] = useState(false);
  const [hasTransactionPassword, setHasTransactionPassword] = useState(false);


  const checkTransactionPasswordStatus = useCallback(async () => {
    try {
      const response = await payoutAPI.hasTransactionPassword();
      setHasTransactionPassword(response.data.hasTransactionPassword);
    } catch (err) {
      console.error('Error checking transaction password status:', err);
    }
  }, []);

  const getTransactionStatusStyle = (payout) => {
    if (payout.status === 'APPROVED') {
      return 'bg-green-100 text-green-800';
    }
    if (payout.status === 'PENDING') {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-red-100 text-red-800';
  };

  const isWithdrawalRequest = (request) => {
    if (request?.requestType) {
      return request.requestType === 'WITHDRAWAL';
    }

    const transactionId = request?.transactionId || '';
    const remarks = (request?.remarks || '').toLowerCase();

    if (transactionId.startsWith('WALLET-TOPUP-') || transactionId.startsWith('PURCHASE-')) {
      return false;
    }

    if (remarks.includes('wallet top-up') || remarks.includes('purchase payment')) {
      return false;
    }

    return true;
  };

  const fetchPayouts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [payoutsResponse, walletResponse] = await Promise.all([
        payoutAPI.getMyPayouts(),
        userAPI.getWallet()
      ]);

      if (payoutsResponse?.data?.success) {
        const withdrawalRequestsOnly = (payoutsResponse.data.withdrawalRequests || []).filter(isWithdrawalRequest);
        setPayouts(withdrawalRequestsOnly);
      } else {
        setPayouts([]);
      }

      // Set wallet data for main wallet
      if (walletResponse?.data?.wallet) {
        setWalletData(walletResponse.data.wallet);
      }
    } catch (err) {
      setError('Failed to load payout history. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayouts();
    checkTransactionPasswordStatus();
  }, [fetchPayouts, checkTransactionPasswordStatus]);

  const handleRequestWithdrawal = async (e) => {
    e.preventDefault();
    
    if (!requestAmount || requestAmount < 1000) {
      setError('Minimum withdrawal amount is Rs. 1000');
      return;
    }

    // Withdrawals are only from mainWallet
    if (walletData && requestAmount > walletData.walletBalance) {
      setError('Insufficient main wallet balance');
      return;
    }

    if (!hasTransactionPassword) {
      setError('Please set your transaction password first');
      setShowRequestModal(false);
      setShowTransactionPasswordModal(true);
      return;
    }

    if (!transactionPassword) {
      setError('Please enter your transaction password');
      return;
    }

    setRequesting(true);
    setError('');

    try {
      const response = await payoutAPI.requestPayout({ 
        amount: parseInt(requestAmount),
        transactionPassword: transactionPassword
      });
      
      if (response?.data?.success) {
        setShowRequestModal(false);
        setRequestAmount('');
        setTransactionPassword('');
        await fetchPayouts(); // Refresh data
      } else {
        setError(response?.data?.error || 'Failed to submit withdrawal request');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit withdrawal request. Please try again.');
      console.error(err);
    } finally {
      setRequesting(false);
    }
  };

  const currentMainWallet = Number(walletData?.walletBalance || 0);
  const canRequestWithdrawal = currentMainWallet >= 1000;

  const handleSetTransactionPassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!transactionPassword || transactionPassword.length < 4) {
      setError('Transaction password must be at least 4 characters');
      return;
    }

    try {
      const response = await payoutAPI.updateTransactionPassword({
        transactionPassword: transactionPassword,
        currentPassword: currentPassword
      });
      
      if (response?.data?.success) {
        setShowTransactionPasswordModal(false);
        setTransactionPassword('');
        setCurrentPassword('');
        setHasTransactionPassword(true);
        alert('Transaction password set successfully!');
      } else {
        setError(response?.data?.error || 'Failed to set transaction password');
      }
    } catch (err) {
      setError('Failed to set transaction password. Please try again.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner fullPage={false} />
      </div>
    );
  }

  if (error && !showRequestModal && !showTransactionPasswordModal) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Wallet Summary */}
      {walletData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Main Wallet Card */}
          <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Main Wallet</span>
              <span className="text-white/70 text-xs">💼</span>
            </div>
            <div className="text-3xl font-bold mb-1">Rs. {walletData?.walletBalance?.toLocaleString() || '0'}</div>
            <div className="text-xs text-white/80 mb-4">Available for withdrawal</div>
            <button
              onClick={() => setShowRequestModal(true)}
              className="w-full bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-lg transition-colors font-semibold"
            >
              Withdraw from Main Wallet
            </button>
          </div>

          {/* Repurchase Wallet Card */}
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Repurchase Wallet</span>
              <span className="text-white/70 text-xs">🔁</span>
            </div>
            <div className="text-3xl font-bold mb-1">Rs. {walletData?.repurchaseWallet?.toLocaleString() || '0'}</div>
            <div className="text-xs text-white/80">5% credited on each withdrawal</div>
          </div>
        </div>
      )}

      {/* Withdrawal Summary Box */}
      {/* {wallet && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">Withdrawal Summary</h3>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="text-white/80">Total Withdrawn:</span>
                  <span className="text-xl font-bold">Rs. {wallet.totalPayout?.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-white/80">Available to Withdraw:</span>
                  <span className="text-xl font-bold">Rs. {wallet.pendingAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="text-6xl opacity-20">
              💰
            </div>
          </div>
        </div>
      )} */}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-secondary">
          Withdrawal Requests
        </h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowRequestModal(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Withdraw from Main Wallet
          </button>
          <button
            onClick={fetchPayouts}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          {payouts.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              No payout records found.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">SR. No</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Gross Amount</th>
                  {/* <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Net Payout (85%)</th> */}
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payouts.map((payout, index) => (
                  <tr key={payout.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">

                      Rs. {payout.amount?.toLocaleString()}
                    </td>
                    {/* <td className="px-6 py-4 font-medium text-green-700">
                      Rs. {(payout.netAmount || (payout.amount * 0.85))?.toLocaleString()}
                    </td> */}
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTransactionStatusStyle(payout)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(payout.requestedAt || payout.createdAt).toLocaleString('en-GB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Request Withdrawal Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-secondary">Withdraw from Main Wallet</h3>
              <button
                onClick={() => { setShowRequestModal(false); setError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleRequestWithdrawal}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Withdrawal Amount (Rs.)
                </label>
                <input
                  type="number"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter amount (minimum Rs. 1000)"
                  min="1000"
                  max={canRequestWithdrawal ? currentMainWallet : undefined}
                  step="100"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Available in Main Wallet: Rs. {currentMainWallet.toLocaleString()}
                </p>
                {!canRequestWithdrawal && (
                  <p className="mt-1 text-sm text-red-600">
                    Minimum withdrawal is Rs. 1000. Your current main wallet balance is insufficient.
                  </p>
                )}
              </div>

              {/* Deduction Breakdown */}
              {requestAmount >= 1000 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="font-semibold text-amber-900 mb-2">Deduction Breakdown</p>
                  <div className="space-y-1 text-amber-800">
                    <div className="flex justify-between">
                      <span>Gross Amount:</span>
                      <span className="font-medium">Rs. {Number(requestAmount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Admin Charge (5%):</span>
                      <span>- Rs. {(requestAmount * 0.05).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>TDS (5%):</span>
                      <span>- Rs. {(requestAmount * 0.05).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-purple-700">
                      <span>Repurchase Wallet (5%):</span>
                      <span>- Rs. {(requestAmount * 0.05).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-amber-300 pt-1 mt-1 flex justify-between font-bold text-green-700">
                      <span>Net Payout to You:</span>
                      <span>Rs. {(requestAmount * 0.85).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Password
                </label>
                <input
                  type="password"
                  value={transactionPassword}
                  onChange={(e) => setTransactionPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your transaction password"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowRequestModal(false); setError(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                {!hasTransactionPassword && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowRequestModal(false);
                      setShowTransactionPasswordModal(true);
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Set Transaction Password
                  </button>
                )}
                <button
                  type="submit"
                  disabled={requesting || !canRequestWithdrawal}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {requesting ? 'Requesting...' : 'Confirm Withdrawal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Password Setup Modal */}
      {showTransactionPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-secondary">Set Transaction Password</h3>
              <button
                onClick={() => setShowTransactionPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                🔐 Transaction password adds an extra layer of security for your withdrawal requests.
              </p>
            </div>

            <form onSubmit={handleSetTransactionPassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your current login password"
                  required
                />
                <p className="mt-2 text-xs text-gray-500">
                  For security verification
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Password
                </label>
                <input
                  type="password"
                  value={transactionPassword}
                  onChange={(e) => setTransactionPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your transaction password (4-20 characters)"
                  minLength={4}
                  maxLength={20}
                  required
                />
                <p className="mt-2 text-xs text-gray-500">
                  Must be 4-20 characters long
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowTransactionPasswordModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Set Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPayouts;